import useHistoryRouter from '@hooks/useLinkTo';
import { getMWebTargetUrl } from '@utils/domainUtil';
import {
  PromotionBrandCollectionBrand,
  PromotionBrandCollectionTab,
  PromotionContent,
} from '@wconcept/wconcept-common/models/renewal/promotion';
import Menu2LineBarItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/Menu2LineBarItem';
import MenuBarItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/MenuBarItem';
import BrandCollectionSection from '@wconcept/wconcept-ui-renewal/components/organisms/_section/BrandCollectionSection';
import React, { useCallback, useMemo, useState } from 'react';
import useGa4Event from '@hooks/useGa4Event';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';
import { PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { isBrandCollectionModule } from '../utils/typeGuards';

interface BrandCollectionModuleProps {
  content: PromotionContent;
  isMobile: boolean;
  bgType?: string;
  visualTokens: PromotionModuleVisualTokens;
  gnbType?: boolean;
}

function BrandCollectionModule({
  content,
  isMobile,
  bgType,
  visualTokens,
  gnbType,
}: BrandCollectionModuleProps) {
  const isDesktop = !isMobile;
  const brandCollectionModule = content.modules?.find(isBrandCollectionModule);
  const { linkTo } = useHistoryRouter();
  const { sendGa4TabClick, sendGa4ButtonClick } = useGa4Event();

  const tabList = brandCollectionModule?.tabList ?? [];

  const [selectedTabId, setSelectedTabId] = useState<string>(
    tabList[0]?.tabId ?? ''
  );

  const currentBrandList =
    tabList.find(
      (tab: PromotionBrandCollectionTab) => tab.tabId === selectedTabId
    )?.brandList ?? [];

  const brandItemList = currentBrandList.map(
    (brand: PromotionBrandCollectionBrand) => ({
      value: brand.brandCd,
      brandImgSrc: brand.imageUrl,
      brandNameKr: brand.brandNameKr,
      brandNameEn: brand.brandName,
      isNew: brand.isNew,
      displayOrder: brand.displayOrder,
    })
  );

  // 현재 선택된 탭 정보
  const currentTab = tabList.find(
    (tab: PromotionBrandCollectionTab) => tab.tabId === selectedTabId
  );
  const currentTabIndex = tabList.findIndex(
    (tab: PromotionBrandCollectionTab) => tab.tabId === selectedTabId
  );

  // 브랜드 모아보기 탭 변경 시 섹션 상단(고정 헤더 바로 아래)으로 스크롤을 맞춰
  // 새로 그려진 grid의 1번 아이템이 상단 고정 내비에 가리지 않고 보이게 한다.
  // 이미 섹션 위쪽(비고정)에 있으면 굳이 스크롤하지 않는다.
  const scrollCollectionToTop = useCallback(() => {
    const tab = document.querySelector(
      '.corner-collection-section .category-tab'
    );
    if (!tab) return;

    const headerHght =
      (
        document.querySelector('#header') ||
        document.querySelector('#headerDesktop .inner-header')
      )?.clientHeight || 0;
    const mainGnbHght =
      document.querySelector('#main-gnb-bar')?.clientHeight || 0;
    const subGnbHght =
      document.querySelector('.sub-gnb-list')?.clientHeight || 0;
    const page = document.querySelector('.mega-promotion-page');

    const getFixedOffset = () => {
      if (page?.classList.contains('is-fix-header')) {
        return mainGnbHght + headerHght + subGnbHght;
      }
      if (gnbType) {
        return mainGnbHght;
      }
      return headerHght;
    };

    // 위로 스크롤하면 헤더가 내려오므로(is-scrollUp), 가려지지 않게 최대 오프셋 기준으로 맞춘다.
    const fixedOffset = getFixedOffset();

    const targetTop =
      tab.getBoundingClientRect().top + window.pageYOffset - fixedOffset;

    // 섹션이 고정된 상태(스크롤이 섹션 상단을 지난 경우)에만 위로 맞춘다.
    if (window.pageYOffset > targetTop) {
      window.scrollTo({ top: targetTop, behavior: 'auto' });
    }
  }, [gnbType]);

  const onClickItem = useCallback(
    (tab: PromotionBrandCollectionTab, index: number) => {
      sendGa4TabClick({
        area: 'promotion_brandlist_tab',
        page: 'promotion',
        listNo: index + 1,
        text: tab.tabName,
      });
      setSelectedTabId(tab.tabId);
      // grid가 새로 그려진 다음 프레임에 섹션 상단으로 스크롤해 1번 아이템 노출
      requestAnimationFrame(() => scrollCollectionToTop());
    },
    []
  );

  const categoryTabList = tabList.map(
    (tab: PromotionBrandCollectionTab, index: number) => (
      <MenuBarItem
        key={tab.tabId}
        value={tab.tabId}
        text={tab.tabName}
        onClick={() => onClickItem(tab, index)}
      />
    )
  );

  const { presetBgType, customColorsFull } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'full',
  });

  const sectionColorType = customColorsFull ?? presetBgType;

  return (
    <BrandCollectionSection
      colorType={sectionColorType}
      rs="250"
      selectedCateTabId={selectedTabId}
      categoryTabType="type1"
      categoryTabList={categoryTabList}
      brandItemList={brandItemList}
      isDesktop={isDesktop}
      onClick={async (value: string) => {
        const clickedBrand = currentBrandList.find(
          (brand: PromotionBrandCollectionBrand) => brand.brandCd === value
        );
        const clickedBrandIndex = currentBrandList.findIndex(
          (brand: PromotionBrandCollectionBrand) => brand.brandCd === value
        );
        sendGa4ButtonClick({
          area: 'promotion_brandlist_button',
          page: 'promotion',
          text: clickedBrand?.brandNameKr || '',
          listNo: clickedBrandIndex + 1,
          bannerType: 'brand',
          pageNo: currentTabIndex + 1,
          tabNm: currentTab?.tabName || '',
          variable: clickedBrand?.isNew ? 'NEW' : undefined,
        });
        const landingUrl = await getMWebTargetUrl('', `/rn/brand/${value}`);
        linkTo.push({
          url: landingUrl,
        });
      }}
    />
  );
}

export default BrandCollectionModule;
